import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Box } from "@mui/material";
import LoginBg from "../../assets/image/login-bg.svg";
import MbLogo from "../../assets/image/mb-logo.svg";

const TUNE_DIAG_DIST = 0;
const TUNE_PERP_DIST = 0;
const TUNE_SCALE = 1.2;
const TUNE_SCALE_ANGLE_FACTOR = 0.9;
const TUNE_ANGLE_OFFSET = 0;
const MB_LOGO_LAYOUT_WIDTH = 100;
const MB_LOGO_DIAGONAL_SCALE_MULT = 1.4;

export default function LoginBackground({ children, title = null, containerSx = {} }) {
  const [boxSize, setBoxSize] = useState({ width: 0, height: 0 });
  const [pageZoomComp, setPageZoomComp] = useState(1);
  const boxRef = useRef(null);

  useEffect(() => {
    const updateSize = () => {
      if (boxRef.current) {
        const rect = boxRef.current.getBoundingClientRect();
        setBoxSize({ width: rect.width, height: rect.height });
      }
    };
    updateSize();
    globalThis.addEventListener("resize", updateSize);
    return () => globalThis.removeEventListener("resize", updateSize);
  }, []);

  useEffect(() => {
    const vv = globalThis.visualViewport;
    if (!vv) {
      setPageZoomComp(1);
      return undefined;
    }
    const syncZoom = () => {
      const scale = vv.scale;
      setPageZoomComp(scale > 0 && Number.isFinite(scale) ? 1 / scale : 1);
    };
    syncZoom();
    vv.addEventListener("resize", syncZoom);
    vv.addEventListener("scroll", syncZoom);
    return () => {
      vv.removeEventListener("resize", syncZoom);
      vv.removeEventListener("scroll", syncZoom);
    };
  }, []);

  const refW = boxSize.width > 0 ? boxSize.width : 1920;
  const refH = boxSize.height > 0 ? boxSize.height : 1080;
  const angleRad = Math.atan2(refH, refW);
  const angleDeg = (angleRad * 180) / Math.PI;
  const refAngleRad = Math.atan2(1080, 1920);
  const scaleBase = (refW / 1920) * TUNE_SCALE;
  const angleDelta = angleRad - refAngleRad;
  const scaleAngleComp = 1 + angleDelta * TUNE_SCALE_ANGLE_FACTOR;
  const scale = scaleBase * scaleAngleComp;
  const diagUx = Math.cos(angleRad);
  const diagUy = Math.sin(angleRad);
  const perpUx = Math.sin(angleRad);
  const perpUy = -Math.cos(angleRad);
  const pivotLeft = refW / 2 + diagUx * TUNE_DIAG_DIST + perpUx * TUNE_PERP_DIST;
  const pivotBottom = refH / 2 - (diagUy * TUNE_DIAG_DIST + perpUy * TUNE_PERP_DIST);
  const mbLogoTransformScale = scale * MB_LOGO_DIAGONAL_SCALE_MULT * pageZoomComp;

  return (
    <Box
      ref={boxRef}
      sx={{
        minHeight: "100vh",
        width: "100vw",
        overflowY: "auto",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        background: "linear-gradient(to bottom right, #0b3d87 0%, #1e62c9 50%, #f4f6f8 50%, #f4f6f8 100%)",
        p: { xs: 2, sm: 3 },
        boxSizing: "border-box",
        ...containerSx,
      }}
    >
      {title ? <title>{title}</title> : null}
      {boxSize.width > 0 && boxSize.height > 0 && (
        <img
          src={LoginBg}
          alt=""
          aria-hidden="true"
          style={{
            position: "absolute",
            bottom: pivotBottom,
            left: pivotLeft,
            zIndex: 1,
            opacity: 0.4,
            transformOrigin: "bottom center",
            transform: `translateX(-50%) rotate(${-angleDeg + TUNE_ANGLE_OFFSET}deg) scale(${scale})`,
            pointerEvents: "none",
            userSelect: "none",
          }}
        />
      )}

      <img
        src={MbLogo}
        alt=""
        aria-hidden="true"
        style={{
          position: "fixed",
          right: 20,
          bottom: 20,
          zIndex: 1,
          width: MB_LOGO_LAYOUT_WIDTH,
          height: "auto",
          objectFit: "contain",
          objectPosition: "right bottom",
          transformOrigin: "right bottom",
          transform: `scale(${mbLogoTransformScale})`,
          opacity: 0.8,
          pointerEvents: "none",
          userSelect: "none",
        }}
      />

      {children}
    </Box>
  );
}

LoginBackground.propTypes = {
  children: PropTypes.node,
  title: PropTypes.string,
  containerSx: PropTypes.object,
};
